import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../style/EditConditionPage.css';
import { useTranslation } from 'react-i18next'; // Import translation hook

const EditConditionPage = () => {
    const { id, conditionId } = useParams(); // Get pet ID and condition ID from URL
    const navigate = useNavigate(); // For redirection after saving
    const { t } = useTranslation(); // Translation function
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const [condition, setCondition] = useState({
        name: '',
        diagnosisDate: '',
        notes: '',
    });
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchConditionDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(t('condition.fetchFailed')); // Use translation for error
                }

                const data = await response.json();
                setCondition({
                    name: data.name,
                    diagnosisDate: data.diagnosisDate,
                    notes: data.notes,
                });
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        fetchConditionDetails();
    }, [id, conditionId, baseUrl, t]);

    const handleSaveCondition = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}`, {
                method: 'PUT', // Use PUT to update condition details
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(condition),
            });

            if (!response.ok) {
                throw new Error(t('condition.updateFailed')); // Use translation for error
            }

            navigate(`/pet/${id}/conditions/${conditionId}`); // Redirect back to the condition details page
        } catch (error) {
            setError(error.message);
            console.error(error);
        }
    };

    const handleDeleteCondition = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}`, {
                method: 'DELETE', // Use DELETE method to remove the condition
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(t('condition.deleteFailed')); // Use translation for error
            }

            navigate(`/pet/${id}`); // Redirect to the pet page after deletion
        } catch (error) {
            setError(error.message);
            console.error(error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCondition((prevCondition) => ({
            ...prevCondition,
            [name]: value,
        }));
    };

    if (error) {
        return <div className="error-message text-red-500 text-center">{t('general.error')}: {error}</div>;
    }

    return (
        <div className="edit-condition-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('condition.titleEdit')}</h2>
            <div className="edit-condition-form space-y-4">
                <label className="block text-dafauna-darkText font-medium">
                    {t('general.name')}
                    <input
                        type="text"
                        name="name"
                        value={condition.name}
                        onChange={handleChange}
                        required
                        className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary mt-2"
                    />
                </label>
                <label className="block text-dafauna-darkText font-medium">
                    {t('condition.diagnosisDate')}
                    <input
                        type="date"
                        name="diagnosisDate"
                        value={condition.diagnosisDate.split('T')[0]} // Format date for input
                        onChange={handleChange}
                        required
                        className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary mt-2"
                    />
                </label>
                <label className="block text-dafauna-darkText font-medium">
                    {t('condition.notes')}
                </label>
                <textarea
                    name="notes"
                    value={condition.notes}
                    onChange={handleChange}
                    className="form-textarea border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary mt-2"
                />
                <button
                    onClick={handleSaveCondition}
                    className="btn save-condition-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                >
                    {t('general.save')}
                </button>
                <button
                    onClick={() => navigate(`/pet/${id}/conditions/${conditionId}`)}
                    className="btn cancel-btn w-full bg-gray-400 text-white rounded-md px-4 py-2 hover:bg-gray-500"
                >
                    {t('general.cancel')}
                </button>
                <button
                    onClick={handleDeleteCondition}
                    className="btn delete-condition-btn w-full bg-red-500 text-white rounded-md px-4 py-2 hover:bg-red-600"
                >
                    {t('general.delete')}
                </button>
            </div>
        </div>
    );
};

export default EditConditionPage;